import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Merges multiple class names and resolves Tailwind CSS class conflicts.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Format date string into friendly relative or localized time for conversation cards/snippets.
 */
export function formatTimestamp(dateString?: string | null): string {
  if (!dateString) return '';
  const trimmed = dateString.trim();
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(trimmed)) {
    return trimmed.length > 5 ? trimmed.slice(0, 5) : trimmed;
  }

  const date = new Date(trimmed);
  if (isNaN(date.getTime())) return '';

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMinutes < 1) return 'Agora';
  if (diffMinutes < 60) return `${diffMinutes}m atrás`;
  if (diffHours < 24 && date.getDate() === now.getDate()) {
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', hour12: false });
  }
  if (diffDays === 1 || (diffHours < 48 && date.getDate() === now.getDate() - 1)) return 'Ontem';
  if (diffDays < 7) {
    return date.toLocaleDateString('pt-BR', { weekday: 'short' });
  }
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

/**
 * Format message timestamp for chat bubbles (always 24-hour pt-BR format: "14:32").
 */
export function formatMessageTimestamp(dateString?: string | null): string {
  if (!dateString) return '';
  const trimmed = dateString.trim();
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(trimmed)) {
    return trimmed.length > 5 ? trimmed.slice(0, 5) : trimmed;
  }

  const date = new Date(trimmed);
  if (isNaN(date.getTime())) return '';

  return date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/**
 * Format WhatsApp-style day divider pill:
 * - "Hoje"
 * - "Ontem"
 * - "21 de outubro" (same year)
 * - "21 de outubro de 2025" (different year)
 */
export function formatDayDivider(dateString?: string | null): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';

  const now = new Date();
  const isToday =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();

  if (isToday) return 'Hoje';

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday =
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear();

  if (isYesterday) return 'Ontem';

  const isCurrentYear = date.getFullYear() === now.getFullYear();
  if (isCurrentYear) {
    return date.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long' });
  }

  return date.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' });
}

/**
 * Extract initials from full name or email.
 */
export function getInitials(name?: string | null, email?: string | null): string {
  if (name && name.trim().length > 0) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return parts[0].slice(0, 2).toUpperCase();
  }
  if (email && email.trim().length > 0) {
    return email.trim().slice(0, 2).toUpperCase();
  }
  return 'CA';
}

/**
 * Resolves the display name for a customer or conversation.
 * When name is not present, displays email; if neither is available, shows 'Anonymous User'.
 */
export function getCustomerDisplayName(
  name?: string | null,
  email?: string | null,
  fallback = 'Usuário Anônimo'
): string {
  if (name && name.trim().length > 0) {
    return name.trim();
  }
  if (email && email.trim().length > 0) {
    return email.trim();
  }
  return fallback;
}


/**
 * Validate and sanitize hex color strings with fallback.
 */
export function sanitizeHexColor(hex: string | null | undefined, fallback = '#2563eb'): string {
  if (!hex) return fallback;
  const clean = hex.trim();
  const regex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
  if (regex.test(clean)) {
    if (clean.length === 4) {
      return `#${clean[1]}${clean[1]}${clean[2]}${clean[2]}${clean[3]}${clean[3]}`.toLowerCase();
    }
    return clean.toLowerCase();
  }
  return fallback;
}

/**
 * Safely extract an array of items from unknown API responses.
 * Handles both plain arrays and wrapped objects like { data: [...] }, { items: [...] }, { stores: [...] }, etc.
 */
export function extractArrayData<T = any>(data: any): T[] {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    if (Array.isArray(data.items)) return data.items;
    if (Array.isArray(data.data)) return data.data;
    if (Array.isArray(data.stores)) return data.stores;
    if (Array.isArray(data.conversations)) return data.conversations;
    if (Array.isArray(data.messages)) return data.messages;
    if (Array.isArray(data.customers)) return data.customers;
  }
  return [];
}

export function extractStoresArray(data: any): any[] {
  const rawList = extractArrayData(data);
  return rawList.map((item: any) => {
    if (!item || typeof item !== 'object') return item;
    const name = item.name || item.storeName || item.StoreName || 'Loja Sem Nome';
    const storeName = item.storeName || item.name || item.StoreName || 'Loja Sem Nome';
    const rawProvider = item.provider || item.platform || item.Platform || '';
    const provider = rawProvider ? String(rawProvider).toLowerCase() : 'store';
    const platform = item.platform || item.provider || item.Platform || 'store';
    const id = item.id || item.Id || item.storeId || item.StoreId || '';
    const status = item.status || 'Connected';
    const connectedAt = item.connectedAt || item.ConnectedAt || new Date().toISOString();

    return {
      ...item,
      id,
      name,
      storeName,
      provider,
      platform,
      status,
      connectedAt,
      storeUrl: item.storeUrl || item.StoreUrl,
      contactEmail: item.contactEmail || item.ContactEmail,
      externalAccountId: item.externalAccountId || item.ExternalAccountId,
    };
  });
}

